import type { AxiosError } from 'axios';
import { oidcConfig } from '../config/oidcConfig';

import { User, UserManager } from 'oidc-client-ts';

class KCHandler {
  public userManager: UserManager;
  public isKeycloak = true;
  public config?: { set: (_key: string, _value: string) => void } | null;

  public constructor() {
    this.userManager = new UserManager(oidcConfig);

    if (process.env.REACT_APP_KC_TOKEN) {
      // only for testing
      this.setUserWithToken(process.env.REACT_APP_KC_TOKEN).catch(error => {
        console.error('Failed to set test user with access token:', error);
      });
    }

    this.userManager.events.addAccessTokenExpiring(async () => {
      const refreshedUser = await this.userManager.signinSilent();

      this.config?.set('accessToken', refreshedUser?.access_token || '');

      document.dispatchEvent(
        new CustomEvent('ecaTokenUpdated', {
          detail: { token: refreshedUser?.access_token },
        })
      );
    });
  }

  public addConfig(
    config: { set: (_key: string, _value: string) => void } | null
  ): void {
    this.config = config;
  }

  public signInCallback(): Promise<User | undefined> {
    return this.userManager.signinCallback();
  }

  public signInSilentCallback(): Promise<void> {
    return this.userManager.signinSilentCallback();
  }

  public async getToken(): Promise<string | undefined> {
    const user = await this.getUser();

    return user?.access_token;
  }

  public async getIdentityToken(): Promise<string | undefined> {
    const user = await this.getUser();

    return user?.id_token;
  }

  public async login(): Promise<void> {
    await this.userManager.signinRedirect();
  }

  public async handleError(error: AxiosError): Promise<void> {
    if (error.response?.data && error.response?.status === 403) {
      console.warn('Error: ', error.response?.data);
    } else if (
      error.response?.status === 401 ||
      error.response?.status === 403
    ) {
      await this.login();
    }
  }

  public async logout(): Promise<void> {
    await this.userManager.signoutRedirect();
  }

  public async setUserWithToken(accessToken: string): Promise<void> {
    const user = new User({
      access_token: accessToken,
      token_type: 'Bearer',
      expires_at: Math.floor(Date.now() / 1000) + 3600, // Set token expiration time
      profile: {
        sub: '',
        iss: oidcConfig.authority,
        aud: oidcConfig.client_id,
        exp: Math.floor(Date.now() / 1000) + 3600, // Token expiration time
        iat: Math.floor(Date.now() / 1000), // Token issued at time
      },
    });

    await this.userManager.storeUser(user);
  }

  private async getUser(): Promise<User | null> {
    const user = await this.userManager.getUser();

    if (!user) {
      await this.login();

      return null;
    }

    const now = Math.floor(Date.now() / 1000);

    if (user?.expires_at && user.expires_at < now) {
      try {
        const refreshedUser = await this.userManager.signinSilent();

        return refreshedUser;
      } catch (error) {
        console.error('Failed to refresh token:', error);
        await this.login();
      }
    }

    return user;
  }
}

export const kcGateway: KCHandler = new KCHandler();
